import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { defineMessages, FormattedMessage } from 'react-intl'
import history from 'src/history'
import { useFormatMessage } from 'src/providers/intl'

// constants
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { ViewShouldYouSellPanel } from 'src/constants/trackingEvents'
import { SHOULD_SELL_VIEW_EVENT } from 'src/constants/client/events'
import { IF_THEY_SHOULD_SELL_TOPIC_KEY } from 'src/constants/topicKeys'
import { QUESTIONS } from 'src/constants/messages'

// components
import { Page, Text, If, Unless, BotHeadSpin } from 'src/Components'
import { OverlayQuestionsFooter } from 'src/Components/shared/Routing'
import { Player } from 'src/Components/shared/Player'
import { SlideOnMount } from 'src/Components/shared/Animations'
import { RedirectToHomeDigest } from 'src/Components/shared/Navigation'

// Hooks
import { useTrackHomeEventOnce } from 'src/hooks'

// selectors
import {
  selectShouldYouSellVideoId,
  selectShouldYouSellVideoType,
} from 'src/store/selectors/videos'
import {
  selectShouldYouSellHeaderIsPositive,
  selectShouldYouSellLoading,
} from 'src/store/selectors/shouldYouSell'
import { selectCustomerFirstName } from 'src/store/selectors/customerProfile'
import { selectHomeownerMarketLoading } from 'src/store/selectors/marketRecord'
import { selectShouldDisplayShouldYouSell } from 'src/store/selectors/display'

// helpers
import { dispatchEvent } from 'src/helpers/tagManager'
import CashPosition from './CashPosition'
import WhatSellFor from './WhatSellFor'
import MarketTempSell from './MarketTempSell'
import Appreciation from './Appreciation'

// styles
import styles from './ShouldYouSell.module.scss'

const ShouldYouSellHeaderText = () => {
  const moreThanTwoPositiveIndicators = useSelector(
    selectShouldYouSellHeaderIsPositive
  )

  return (
    <>
      <If condition={moreThanTwoPositiveIndicators}>
        <Text
          size='l'
          className={styles.header}
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='ShouldYouSell.positive.heading'
            defaultMessage='There are strong signals that it might be a good time to sell'
          />
        </Text>
      </If>
      <Unless condition={moreThanTwoPositiveIndicators}>
        <Text
          size='l'
          className={styles.header}
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='ShouldYouSell.negative.heading'
            defaultMessage='These signals indicate it may not be an ideal time to sell'
          />
        </Text>
      </Unless>
    </>
  )
}

const ShouldYouSell = () => {
  const intlFormat = useFormatMessage()
  const customerFirstName = useSelector(selectCustomerFirstName)

  const shouldYouSellVideoId = useSelector(selectShouldYouSellVideoId)
  const shouldYouSellVideoType = useSelector(selectShouldYouSellVideoType)

  const display = useSelector(selectShouldDisplayShouldYouSell)

  const loadingShouldYouSell = useSelector(selectShouldYouSellLoading)
  const loadingMarketData = useSelector(selectHomeownerMarketLoading)
  const loading = loadingShouldYouSell || loadingMarketData
  const trackViewedEvent = useTrackHomeEventOnce(SHOULD_SELL_VIEW_EVENT)

  useEffect(() => {
    trackViewedEvent()
    dispatchEvent(ViewShouldYouSellPanel)
  }, [])

  const handleMarketNav = () => {
    history.replace(OVERLAY_HELPERS.marketSpotlight.buildPath())
  }

  if (!display) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page>
      <Page.Body>
        <If condition={loading}>
          <BotHeadSpin panelSpacing />
        </If>
        <If condition={!loading}>
          <SlideOnMount>
            <ShouldYouSellHeaderText />
          </SlideOnMount>
          <SlideOnMount>
            <Appreciation />
          </SlideOnMount>
          <SlideOnMount>
            <CashPosition />
          </SlideOnMount>
          <SlideOnMount>
            <MarketTempSell doMarketNav={handleMarketNav} />
          </SlideOnMount>
          <SlideOnMount>
            <If condition={shouldYouSellVideoId && shouldYouSellVideoType}>
              <Text
                size='l'
                className={styles.videoHeader}
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='ShouldYouSell.video.heading'
                  defaultMessage="{name}'s strategy on selling:"
                  values={{
                    name: customerFirstName,
                  }}
                />
              </Text>
              <Player
                videoId={shouldYouSellVideoId}
                videoType={shouldYouSellVideoType}
              />
            </If>
          </SlideOnMount>
          <SlideOnMount>
            <WhatSellFor />
          </SlideOnMount>
        </If>
        <Page.Disclaimer />
      </Page.Body>
      <Page.Footer>
        <OverlayQuestionsFooter
          topic={intlFormat(QUESTIONS.shouldYouSellFooterTopic)}
          topicKey={IF_THEY_SHOULD_SELL_TOPIC_KEY}
          questions={Object.values(questionMessages)}
          placeholder
          disableCospo
        />
      </Page.Footer>
    </Page>
  )
}

const questionMessages = defineMessages({
  maximizeListingPrice: {
    id: 'ShouldYouSell.questions.maximize.listing.price',
    defaultMessage: 'How can I prepare my home to maximize my listing price?',
  },
  marketForBuying: {
    id: 'ShouldYouSell.questions.buying.market',
    defaultMessage: "How's the market for buying?",
  },
})

export default ShouldYouSell
